/**
 * MOS access-token expiry helpers.
 * Matches oidc-client-ts default accessTokenExpiringNotificationTimeInSeconds (60).
 * MOS access tokens are ~5 minutes today — renew slightly before that window.
 */
export const MOS_ACCESS_TOKEN_RENEW_WINDOW_SECONDS = 60;

const BEARER_PREFIX = 'Bearer ';

export const getEpochTimeInSeconds = (nowMs = Date.now()) => Math.floor(nowMs / 1000);

export const getMosAccessTokenExpiresInSeconds = (
  user,
  nowEpochSeconds = getEpochTimeInSeconds(),
) => {
  if (typeof user?.expires_at !== 'number') {
    return undefined;
  }

  return user.expires_at - nowEpochSeconds;
};

export const isMosAccessTokenExpired = (user, nowEpochSeconds = getEpochTimeInSeconds()) => {
  const expiresInSeconds = getMosAccessTokenExpiresInSeconds(user, nowEpochSeconds);

  if (typeof expiresInSeconds !== 'number') {
    return false;
  }

  return expiresInSeconds <= 0;
};

export const isMosAccessTokenExpiredOrWithinRenewWindow = (
  user,
  nowEpochSeconds = getEpochTimeInSeconds(),
  renewWindowSeconds = MOS_ACCESS_TOKEN_RENEW_WINDOW_SECONDS,
) => {
  const expiresInSeconds = getMosAccessTokenExpiresInSeconds(user, nowEpochSeconds);

  if (typeof expiresInSeconds !== 'number') {
    return false;
  }

  return expiresInSeconds <= renewWindowSeconds;
};

export const hasMosRefreshToken = (user) => Boolean(user?.refresh_token);

export const getAccessTokenFromAuthorizationHeader = (authorization) => {
  if (!authorization || typeof authorization !== 'string') {
    return undefined;
  }

  if (!authorization.startsWith(BEARER_PREFIX)) {
    return authorization;
  }

  return authorization.slice(BEARER_PREFIX.length);
};

/**
 * One 401 retry for MOS only when the failure looks like access-token expiry
 * (or a race with background renew). Bootstrap / unmapped-user 401s are not a MOS
 * session yet (authProvider is still unset) and must not enter a refresh loop.
 */
export const shouldRetryMosUnauthorized = ({
  isMosSession,
  alreadyRetried,
  user,
  sentAuthorization,
  nowEpochSeconds = getEpochTimeInSeconds(),
}) => {
  if (alreadyRetried) {
    return false;
  }

  if (!isMosSession) {
    return false;
  }

  if (!hasMosRefreshToken(user)) {
    return false;
  }

  const currentAccessToken = user?.access_token;
  const sentAccessToken = getAccessTokenFromAuthorizationHeader(sentAuthorization);

  if (currentAccessToken && sentAccessToken && currentAccessToken !== sentAccessToken) {
    return true;
  }

  return isMosAccessTokenExpiredOrWithinRenewWindow(user, nowEpochSeconds);
};

/**
 * Legacy API sessions must not read or renew MOS tokens.
 * MOS callback/complete still must, even if a LoopBack token is leftover.
 */
export const shouldResolveMosAccessToken = ({ isLegacySession, isMosBootstrapPath }) => {
  if (!isLegacySession) {
    return true;
  }

  return Boolean(isMosBootstrapPath);
};
